import React, { useContext, useState, useCallback, useEffect, useRef } from "react";
import {
  calculateWinData,
  calculateWinDataForLimit6To12,
  calculateWinDataForLimit4To17,
  calculateWinDataForLimit5To13,
} from "../components/Calculations";
import api from "../context/api";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/ShortId.css";

const arraysEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }
  return true;
};

const BOOKING_CODES_LIMIT = 1000;
const BOOKING_CODES_CACHE_KEY = "shortId_booking_codes_cache_v1";
const BOOKING_CODES_CACHE_TTL_MS = 60 * 1000;

const sameBookingCodes = (next = [], prev = []) => {
  if (next.length !== prev.length) return false;
  for (let i = 0; i < next.length; i++) {
    const nextItem = next[i] || {};
    const prevItem = prev[i] || {};
    if (nextItem.id !== prevItem.id) return false;
    if (nextItem.NextGameDate !== prevItem.NextGameDate) return false;
    if (nextItem.closingTime !== prevItem.closingTime) return false;
  }
  return true;
};

const ShortId = () => {
  const [shortId, setShortId] = useState("");
  const [bookingCodes, setBookingCodes] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [data, setData] = useState(null);
  const [sharingItem, setSharingItem] = useState(null);
  const popupRef = useRef(null);
  const [localAmount, setLocalAmount] = useState(5);
  const [localPrice, setLocalPrice] = useState(0);
  const [localMinimumWin, setLocalMinimumWin] = useState(0);
  const [localMaximumWin, setLocalMaximumWin] = useState(0);
  const { token } = useContext(AuthContext);
  const inputRef = useRef(null);
  const amountInputRef = useRef(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: "", onConfirm: null });
  const navigate = useNavigate();
  const location = useLocation();

  const showToast = (message, type = "info") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const showConfirm = (message, onConfirm) => {
    setConfirmDialog({ show: true, message, onConfirm });
  };

  // Handle booking code from login redirect
  useEffect(() => {
    if (location.state?.bookingCode && token) {
      setShortId(location.state.bookingCode);
      setModalVisible(true);
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
      // Auto-fetch after a short delay to ensure modal is open
      setTimeout(() => {
        fetchData(location.state.bookingCode);
      }, 300);
    }
  }, [location.state, token]);

  // Keyboard avoiding behavior for mobile
  useEffect(() => {
    const handleFocus = (e) => {
      if (window.innerWidth <= 768) {
        setTimeout(() => {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    };

    const inputs = document.querySelectorAll('.shortid-input, input[type="number"]');
    inputs.forEach(input => {
      input.addEventListener('focus', handleFocus);
    });

    return () => {
      inputs.forEach(input => {
        input.removeEventListener('focus', handleFocus);
      });
    };
  }, [modalVisible, data]);

  const copyToClipboard = async (textParam) => {
    const text = textParam || (data && data.id ? String(data.id) : "");
    if (!text) return;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("copy-failed");
      }
      showToast("Copied to clipboard!", "success");
    } catch (err) {
      console.error("copyToClipboard failed", err);
      showToast("Failed to copy!", "error");
    }
  };

  const formatAmount = (amount) => {
    return parseFloat(amount).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const fetchData2 = async () => {
    if (!token) {
      navigate("/login", { state: { intendedShortId: shortId } });
      return;
    }
    await fetchData(shortId);
  };

  const fetchData = async (id = shortId) => {
    const normalizedId = String(id || "").trim();
    if (!normalizedId) return;

    try {
      const response = await api.get(`/shortId/${encodeURIComponent(normalizedId)}`);
      const responseData = response.data;
      if (responseData.expired) {
        showToast("This ID has expired.", "error");
      } else {
        setData(responseData);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setData(null);
        showToast("Invalid ID. ID is case sensitive!!", "error");
      } else if (error.response && error.response.status === 400) {
        setData(null);
        showToast("This ID has expired.", "error");
      }
    }
  };

  const saveDataToDatabase = async (derivedValues) => {
    try {
      // compute derived synchronously to avoid stale state (or use provided)
      const { price, minimumWin, maximumWin } = derivedValues || calculateDerived(localAmount, data);
      // Build payload overriding amount/price/minimum/maximum with computed values
      const payload = {
        ...data,
        amount: Number(localAmount),
        price: Number(price),
        minimumWin: Number(minimumWin),
        maximumWin: Number(maximumWin),
      };
      // Helpful debug when the backend saved stale values — remove in production
      const response = await api.post(`/saveNumbers`, payload);
      return response.status === 200;
    } catch (error) {
      console.error('saveDataToDatabase error', error);
      return false;
    }
  };

  const confirm = async () => {
    if (!data) return;
    // normalize selected numbers for display
    let displayNumbers = "";
    if (Array.isArray(data.selectedNumbers)) displayNumbers = data.selectedNumbers.join(", ");
    else if (typeof data.selectedNumbers === "string") {
      try {
        const parsed = JSON.parse(data.selectedNumbers);
        displayNumbers = Array.isArray(parsed) ? parsed.join(", ") : data.selectedNumbers;
      } catch (e) {
        displayNumbers = data.selectedNumbers;
      }
    }

    // compute derived values synchronously so the confirmation matches what we will send
    const derived = calculateDerived(localAmount, data);

    // Build the final payload we intend to persist with a NEW booking shortId
    const newShortId = `H${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`.toUpperCase();
    const payload = {
      ...data,
      // ensure we do not reuse the old identifiers
      id: newShortId,
      shortId: newShortId,
      amount: Number(localAmount),
      price: Number(derived.price),
      minimumWin: Number(derived.minimumWin),
      maximumWin: Number(derived.maximumWin),
      // Track that this game was created using an existing booking code for referral commission
      usedFromCode: data.id,
    };

    const confirmMessage = `Selected Numbers: ${displayNumbers}\nAmount: ${formatAmount(payload.amount)}\nPrice: ${formatAmount(payload.price)}\nMinimum Win: ${formatAmount(
      payload.minimumWin
    )}\nMaximum Win: ${formatAmount(payload.maximumWin)}\nGame Type: ${
      data.selectedDay
    }\nDraw Date: ${data.NextGameDate}\nNew Game ID: ${payload.id}`;

    showConfirm(confirmMessage, async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await api.post(`/saveNumbers`, payload, { headers });
        if (res.status === 200) {
          setModalVisible(false);
          setData(null);
          showToast("Game played successfully!", "success");
        } else {
          showToast("Failed to Play Game. Please try again.", "error");
        }
      } catch (err) {
        console.error("confirm/saveNumbers error", err);
        showToast("Failed to Play Game. Please try again.", "error");
      }
    });
  };

    const fetchBookingCodes = async () => {
    try {
      const cachedRaw = localStorage.getItem(BOOKING_CODES_CACHE_KEY);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        const now = Date.now();
        if (cached?.timestamp && now - cached.timestamp < BOOKING_CODES_CACHE_TTL_MS) {
          const cachedData = Array.isArray(cached.data) ? cached.data : [];
          if (!sameBookingCodes(cachedData, bookingCodes)) {
            setBookingCodes(cachedData);
          }
          return;
        }
      }

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await api.get("/history1", {
        headers,
        params: {
          limit: BOOKING_CODES_LIMIT,
        },
      });
      const history = response.data.historyData || [];
      // Avoid unnecessary state update
      if (!sameBookingCodes(history, bookingCodes)) {
        setBookingCodes(history);
      }
      localStorage.setItem(
        BOOKING_CODES_CACHE_KEY,
        JSON.stringify({
          data: history,
          timestamp: Date.now(),
        })
      );
   } catch (error) {
  console.error("Failed to fetch booking codes:", error);
}
    }



  useEffect(() => {
    if (modalVisible) {
      fetchBookingCodes();
    }
  }, [modalVisible]);

  // Close share popup when clicking outside
  useEffect(() => {
    function handleDocClick(e) {
      if (!sharingItem) return;
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setSharingItem(null);
      }
    }
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, [sharingItem]);

  // When the receipt `data` opens, initialize local amount and derived values
  useEffect(() => {
    if (!data) return;
    // derive an initial amount: if data.lines present use price/lines, otherwise fallback to 5
    const lines = data.lines || (Array.isArray(data.selectedNumbers) ? data.selectedNumbers.length : 1);
    const initialAmount = (data.price && lines) ? Number((data.price / lines).toFixed(2)) : (data.amount ? Number(data.amount) : 5);
    setLocalAmount(initialAmount);
    // compute derived values
    computeDerived(initialAmount);
  }, [data]);

  const computeDerived = (amount) => {
    if (!data) return;
    // computeDerived is now delegated to a pure function calculateDerived
    const { price, minimumWin, maximumWin } = calculateDerived(amount, data);
    setLocalPrice(price);
    setLocalMinimumWin(minimumWin);
    setLocalMaximumWin(maximumWin);
  };

  // pure synchronous calculation used to avoid any timing/state issues
  const calculateDerived = (amount, srcData) => {
    const amt = Number(amount) || 0;
    const d = srcData || data;
    if (!d) return { price: 0, minimumWin: 0, maximumWin: 0 };

    // normalize selectedNumbers to an array
    let selectedNumbersArr = [];
    if (Array.isArray(d.selectedNumbers)) selectedNumbersArr = d.selectedNumbers;
    else if (typeof d.selectedNumbers === 'string') {
      try {
        selectedNumbersArr = JSON.parse(d.selectedNumbers);
      } catch (e) {
        selectedNumbersArr = d.selectedNumbers.split(/\s*,\s*/).filter(Boolean);
      }
    }
    const n = selectedNumbersArr.length;

    // normalize limit to array of numbers
    let limitArr = [];
    if (Array.isArray(d.limit)) limitArr = d.limit.map(Number);
    else if (typeof d.limit === 'string') {
      try {
        limitArr = JSON.parse(d.limit).map(Number);
      } catch (e) {
        limitArr = d.limit.split(/\s*,\s*/).map(s => Number(s));
      }
    }
    const limit = limitArr.length ? limitArr : [0, 0];

    // normalize againstNumbers
    let againstArr = [];
    if (Array.isArray(d.againstNumbers)) againstArr = d.againstNumbers;
    else if (typeof d.againstNumbers === 'string') {
      try {
        againstArr = JSON.parse(d.againstNumbers);
      } catch (e) {
        againstArr = d.againstNumbers.split(/\s*,\s*/).filter(Boolean);
      }
    }

    // branching logic (same as before) but returns values instead of setting state
    if (n === 2 && Number(limit[1]) === 2) {
      return { price: amt, minimumWin: amt * 220, maximumWin: 0 };
    } else if (n === 3 && limit[1] === 3) {
      return { price: amt, minimumWin: amt * 1800, maximumWin: 0 };
    } else if (n >= 3 && n <= 24 && limit[0] === 3 && limit[1] === 24) {
      const calculatedLines = ((n - 1) * n) / 2;
      const price = calculatedLines * amt;
      const minimumWin = amt * 220;
      const maximumWin = n <= 5 ? calculatedLines * 220 * amt : 2200 * amt;
      return { price, minimumWin, maximumWin };
    } else if (n >= 4 && n <= 17 && limit[0] === 4 && limit[1] === 17) {
      const { calculatedLines, minimumWin, price, maximumWin } = calculateWinDataForLimit4To17(n, amt);
      return { price, minimumWin, maximumWin };
    } else if (n >= 5 && n <= 13 && limit[0] === 5 && limit[1] === 13) {
      const { calculatedLines, minimumWin, price } = calculateWinDataForLimit5To13(n, amt);
      return { price, minimumWin, maximumWin: 0 };
    } else if (n >= 6 && n <= 12 && limit[0] === 6 && limit[1] === 12) {
      const { calculatedLines, minimumWin, price } = calculateWinDataForLimit6To12(n, amt);
      return { price, minimumWin, maximumWin: 0 };
    } else if (n === 1 && limit[1] === 1) {
      const calculatedLines = 89;
      return { price: amt * calculatedLines, minimumWin: amt * 880, maximumWin: 0 };
    } else if (limit[0] === 1 && limit[1] === 24) {
      const againstLen = againstArr.length;
      const selectedLen = selectedNumbersArr.length;
      const { calculatedLines, minimumWin, price, maximumWin } = calculateWinData(againstLen, selectedLen, n, amt);
      return { price, minimumWin, maximumWin };
    }

    // fallback to data-provided values
    return { price: Number(d.price) || 0, minimumWin: Number(d.minimumWin) || 0, maximumWin: Number(d.maximumWin) || 0 };
  };

  const handlePress = (id) => {
    if (!token) {
      navigate("/login", { state: { intendedBookingId: id } });
      return;
    }
    fetchData(id);
  };

  const shareItem = async (item) => {
    const message = `Booking Code: ${item.id}\nGAME: ${item.selectedDay}\nPlay on: https://huntexlotto.com`;
    try {
      // Prefer the Web Share API on mobile if available
      if (navigator.share && typeof navigator.share === "function") {
        await navigator.share({ text: message });
        return;
      }

      // Otherwise try Clipboard API or fallback to textarea
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        await navigator.clipboard.writeText(message);
        alert("Booking code copied to clipboard!");
        return;
      }

      // As a better fallback for mobile web, open WhatsApp app directly
      const waUrl = `whatsapp://send?text=${encodeURIComponent(message)}`;
      window.location.href = waUrl;
      return;

      // If popup blocked or wa.me not available, fallback to textarea copy
      const ta = document.createElement("textarea");
      ta.value = message;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (!ok) throw new Error("copy-failed");
      alert("Booking code copied to clipboard!");
    } catch (err) {
      console.error("shareItem failed", err);
      // If everything fails, copy to clipboard as final fallback and notify user
      try {
        const ta = document.createElement("textarea");
        ta.value = message;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        alert("Booking code copied to clipboard! Paste it in your app to share.");
      } catch (e) {
        alert("Failed to share booking code. Please copy it manually: " + message);
      }
    }
  };

  const getLimitLabel = useCallback((limit) => {
    if (!Array.isArray(limit) || limit.length === 0) {
      return "";
    }
    const numericLimit = limit.map((item) =>
      typeof item === "string" ? parseFloat(item) : item
    );
    if (arraysEqual(numericLimit, [1, 2])) return "DIRECT 2";
    if (arraysEqual(numericLimit, [1, 3])) return "DIRECT 3";
    if (arraysEqual(numericLimit, [3, 24])) return "PERM 2";
    if (arraysEqual(numericLimit, [4, 17])) return "PERM 3";
    if (arraysEqual(numericLimit, [5, 13])) return "PERM 4";
    if (arraysEqual(numericLimit, [6, 12])) return "PERM 5";
    if (arraysEqual(numericLimit, [1, 1])) return "Banker Against All";
    if (arraysEqual(numericLimit, [1, 24])) return "Banker Against";
    return "";
  }, []);

  const BookingCodeItem = ({ item, onPress }) => (
    <div className="booking-card" onClick={() => onPress(item.id)}>
      <div className="booking-info">
        <span>{item.selectedDay} - {item.closingTime}</span>
        <span>Game: {getLimitLabel(item.limit)}</span>
      </div>
      <div className="booking-numbers">
        {Array.isArray(item.selectedNumbers) &&
          item.selectedNumbers.map((num, idx) => (
            <span className="booking-number" key={idx}>{num}</span>
          ))}
      </div>
      <div style={{ position: 'relative' }}>
        <button className="share-btn" onClick={(e) => { e.stopPropagation(); setSharingItem(prev => (prev && prev.id === item.id) ? null : item); }}>
          Share: <span className="share-code">{item.id}</span>
        </button>

        {sharingItem && sharingItem.id === item.id && (
          <div className="share-popup" ref={popupRef} onClick={(e) => e.stopPropagation()}>
            <button className="share-option wa" onClick={() => { const msg = `Booking Code: ${item.id} - GAME: ${item.selectedDay} - Play on: https://huntexlotto.com`; const url = `whatsapp://send?text=${encodeURIComponent(msg)}`; window.location.href = url; setSharingItem(null); }}>
              <svg className="share-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.52 3.48a11 11 0 10-15.56 15.56L2 22l3.0-1.06A11 11 0 0020.52 3.48z" stroke="#25D366" strokeWidth="1.2" fill="#25D366"/></svg>
              WhatsApp
            </button>
           
            <button className="share-option sms" onClick={() => { const msg = `Booking Code: ${item.id} - GAME: ${item.selectedDay} - Play on: https://huntexlotto.com`; const sms = `sms:?body=${encodeURIComponent(msg)}`; window.location.href = sms; setSharingItem(null); }}>
              <svg className="share-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z" fill="#333"/></svg>
              SMS
            </button>
           
            <button className="share-option copy" onClick={async () => { const text = `Booking Code: ${item.id} - GAME: ${item.selectedDay} - Play on: https://huntexlotto.com`; await copyToClipboard(text); setSharingItem(null); }}>
              <svg className="share-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 1H4a2 2 0 00-2 2v14h2V3h12V1zM20 5H8a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2zm0 16H8V7h12v14z" fill="#117864"/></svg>
              Copy
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
        <button
      className="shortid-fab"
      title="Open Booking Code"
      onClick={() => setModalVisible(true)}
      style={{
        background: "none",
        border: "none",
        outline: "none",
        cursor: "pointer",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "2rem",
        color: "#fff"
      }}
    >
      <span role="img" aria-label="Booking Code">🔑</span>
    </button>
      {modalVisible && (
        <div className="modal-overlay" onClick={() => setModalVisible(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button
              className="close"
              onClick={() => {
                if (data) {
                  setData(null);
                } else {
                  setModalVisible(false);
                }
              }}
            >
              ×
            </button>
            {!data ? (
              <>
                <div className="notice">PASTE BOOKING CODE TO PROCEED</div>
                <input
                  ref={inputRef}
                  className="shortid-input"
                  placeholder="Enter Booking Code"
                  value={shortId}
                  onChange={e => setShortId(e.target.value)}
                />
                <button
                  className="fetch-btn"
                  onClick={fetchData2}
                  disabled={shortId.trim().length === 0}
                >
                  Search Code
                </button>
                <div className="booking-list-marquee">
      <div className="booking-list-inner">
        {bookingCodes.map((item, idx) => (
          <BookingCodeItem item={item} onPress={handlePress} key={item.id + idx} />
        ))}
      </div>
    </div>
              </>
            ) : null}
            {data ? (
              <div className="receipt-container">
                <div className="receipt-title">
                  <span>YOUR RECEIPT</span>  
                </div>
                <div className="receipt-info">
                  <div>Selected Numbers: {Array.isArray(data.selectedNumbers) ? data.selectedNumbers.join(", ") : "None"}</div>
                  <div style={{ marginTop: 8 }}>
                    <label style={{ fontWeight: 'bold', marginRight: 8 }}>Amount:</label>
                    <input
                      ref={amountInputRef}
                      type="number"
                      min="0.2"
                      step="0.1"
                      value={localAmount}
                      onChange={e => {
                        const raw = e.target.value;
                        if (raw === "") {
                          setLocalAmount("");
                          return;
                        }
                        const v = parseFloat(raw);
                        if (Number.isNaN(v)) return;
                        setLocalAmount(v);
                        computeDerived(v);
                      }}
                      onBlur={e => {
                        let v = parseFloat(e.target.value);
                        if (Number.isNaN(v) || v < 0.2) v = 0.2;
                        setLocalAmount(v);
                        computeDerived(v);
                      }}
                      style={{ width: 100, padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc' }}
                    />
                  </div>
                  <div>Minimum Win: {formatAmount(localMinimumWin)}</div>
                  <div>Maximum Win: {formatAmount(localMaximumWin)}</div>
                  <div>Price: {formatAmount(localPrice)}</div>
                  <div>Game Date: {data.selectedDay}</div>
                  <div>Draw Date: {data.NextGameDate}</div>
                  <div>Game Type: {getLimitLabel(data.limit)}</div>
                  <div className="share-container">
                    <b>Share Booking Code:</b>
                    <button className="copy-btn" onClick={() => copyToClipboard(String(data.id))}>
                       Code: {data.id} <span className="copy-label">(Copy)</span>
                    </button>
                  </div>
                  <button className="play-btn" onClick={confirm}>Play Game</button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.show && (
        <div className="confirm-overlay" onClick={() => setConfirmDialog({ show: false, message: "", onConfirm: null })}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Confirm Game</h3>
            <div className="confirm-message">
              {confirmDialog.message.split('\\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
            <div className="confirm-buttons">
              <button 
                className="confirm-btn cancel-btn"
                onClick={() => setConfirmDialog({ show: false, message: "", onConfirm: null })}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn proceed-btn"
                onClick={() => {
                  setConfirmDialog({ show: false, message: "", onConfirm: null });
                  if (confirmDialog.onConfirm) confirmDialog.onConfirm();
                }}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShortId;